import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Divider,
  Skeleton,
  Alert,
} from "@mui/material";
import { settingsApi, AppSettings } from "../../services/api";

export default function CalculatorPage() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasePrice, setPurchasePrice] = useState("");
  const [salePrice, setSalePrice] = useState("");

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => setSettings(res.data))
      .catch(() => setError("Failed to load settings."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Profit Calculator
        </Typography>
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (error || !settings) {
    return <Alert severity="error">{error || "Unknown error"}</Alert>;
  }

  const cost = Number(purchasePrice) || 0;
  const price = Number(salePrice) || 0;
  const ccRate = Number(settings.credit_card_fee_rate);
  const commissionRate = Number(settings.booth_commission_rate);

  const ccFee = price * ccRate;
  const commission = price * commissionRate;
  const totalFees = ccFee + commission;
  const afterFees = price - totalFees;
  const profit = afterFees - cost;

  const fmt = (n: number) => `$${n.toFixed(2)}`;
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Profit Calculator
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent
          sx={{
            p: { xs: 2, md: 3 },
            "&:last-child": { pb: { xs: 2, md: 3 } },
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter what you paid and a sale price to see your true profit after
            fees and store commission.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Purchase Price"
              type="number"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              fullWidth
              autoFocus
              inputProps={{ step: "0.01", min: "0", inputMode: "decimal" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
              placeholder="0.00"
            />
            <TextField
              label="Sale Price"
              type="number"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              fullWidth
              inputProps={{ step: "0.01", min: "0", inputMode: "decimal" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
              placeholder="0.00"
            />
          </Box>
        </CardContent>
      </Card>

      {price > 0 && (
        <Card>
          <CardContent
            sx={{
              p: { xs: 2, md: 3 },
              "&:last-child": { pb: { xs: 2, md: 3 } },
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              gutterBottom
            >
              Breakdown
            </Typography>

            <Row label="Sale Price" value={fmt(price)} />
            <Divider sx={{ my: 1 }} />
            <Row
              label={`CC Fee (${pct(ccRate)})`}
              value={`- ${fmt(ccFee)}`}
              color="error.main"
            />
            <Row
              label={`Store Commission (${pct(commissionRate)})`}
              value={`- ${fmt(commission)}`}
              color="error.main"
            />
            <Divider sx={{ my: 1 }} />
            <Row
              label="Total Fees"
              value={`- ${fmt(totalFees)}`}
              color="error.main"
              bold
            />
            <Row
              label="After Fees"
              value={fmt(afterFees)}
              bold
            />
            {cost > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Row
                  label="Purchase Price"
                  value={`- ${fmt(cost)}`}
                  color="error.main"
                />
              </>
            )}
            <Divider sx={{ my: 1.5 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: profit >= 0 ? "success.50" : "error.50",
                borderRadius: 2,
                p: 1.5,
                border: "1px solid",
                borderColor: profit >= 0 ? "success.light" : "error.light",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                True Profit
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                color={profit >= 0 ? "success.main" : "error.main"}
              >
                {fmt(profit)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function Row({
  label,
  value,
  color,
  bold,
}: {
  label: string;
  value: string;
  color?: string;
  bold?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        py: 0.5,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={bold ? 600 : 400}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={bold ? 700 : 600}
        color={color || "text.primary"}
      >
        {value}
      </Typography>
    </Box>
  );
}
